import {
  Button, Grid, Stack,
} from '@mui/material';
import moment from 'moment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InfoOutlined } from '@mui/icons-material';
import useToggle from '../../hooks/useToggle';
import { ConfirmDialogBox } from '../../components/CustomDialogBox';
import { getTaxonomyList, deleteTaxonomy } from '../../features';
import { CustomIcon } from '../../components/CustomIcon';
import { CustomDataGrid } from '../../components/CustomDataGrid';
import { PackageHeader } from '../../components/CustomHeading';
import { TITLES } from '../../utils/constants';
import useStyles from '../../hooks/useStyles';
import styles from '../../layouts/pages/style';

function Taxonomy() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const taxonomyList = useSelector((state) => state.taxonomyList?.data);
  const [open, setOpen] = useToggle(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    dispatch(getTaxonomyList());
  }, []);

  const handleDeleteTaxonomy = (id) => {
    dispatch(deleteTaxonomy({ id })).then(() => dispatch(getTaxonomyList()));
    setSelectedId(null);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Label',
      minWidth: 300,
      flex: 1,
      sortable: true,
    },
    {
      field: 'updatedAt',
      headerName: 'Last Modified',
      width: 60,
      minWidth: 100,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'center-align--header',
      sortable: true,
      renderCell: (params) => moment(params.value).fromNow(),
    },
    {
      field: 'Actions',
      headerName: 'Actions',
      width: 450,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row">
          <CustomIcon
            size="small"
            Icon={EditIcon}
            onClick={() => navigate(`/taxonomy/${params.row.id}`)}
          />

          <CustomIcon
            size="small"
            Icon={DeleteIcon}
            onClick={() => setSelectedId(params.row.id)}
          />
        </Stack>
      ),
    },
  ];

  const classes = useStyles(styles)();

  return (
    <Grid container gap={2}>
      <Grid item xs={12} className={classes.cardStyles}>
        <Stack className={classes.headerStyles}>
          <PackageHeader noPackage heading={TITLES.TAXONOMY_BUILDER} />
          <Stack direction="row" gap={1} alignItems="center">
            <CustomIcon size="small" Icon={InfoOutlined} onClick={setOpen} />
            <Button variant="outlined" onClick={() => navigate('/taxonomy/new')}>New Taxonomy</Button>
          </Stack>
        </Stack>
      </Grid>

      <Grid item className={classes.cardStyles} xs={12}>
        <CustomDataGrid
          columns={columns}
          rows={taxonomyList}
          rowId="id"
          footer
          initialState={{
            sorting: {
              sortModel: [{ field: 'updatedAt', sort: 'desc' }],
            },
          }}
        />
      </Grid>

      <ConfirmDialogBox
        openDialog={selectedId}
        title="Confirm Delete"
        message="Are you sure you want to delete this taxonomy? This action cannot be undone."
        onClose={() => setSelectedId(null)}
        onConfirm={() => {
          if (selectedId) handleDeleteTaxonomy(selectedId);
          return null;
        }}
        cancelText="Cancel"
        confirmText="Delete"
      />

      <ConfirmDialogBox
        openDialog={open}
        title="Taxonomy"
        message="Organize your features into meaningful categories to better understand usage patterns. Group by function, role, or maturity stage for clearer insights. Start by defining levels of grouping—add sub-levels to build a deeper hierarchy. Once your taxonomy is ready, link each node to features from your existing list to complete the structure."
        onClose={setOpen}
        cancelText="OK"
      />
    </Grid>
  );
}

export default Taxonomy;
